import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExploreIcon from '@mui/icons-material/Explore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import React from 'react'

interface JoinOfficeInstructionsStepProps {
  onBack: () => void
  onExit: () => void
}

/**
 * JoinOfficeInstructionsStep - Provides instructions for users who want to join an existing office
 *
 * Displays information about contacting office admins and how invites work.
 * Offers options to return to choices or exit and explore the app.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5
 */
const JoinOfficeInstructionsStep: React.FC<JoinOfficeInstructionsStepProps> = ({
  onBack,
  onExit
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 600,
            mb: 2,
            color: 'text.primary'
          }}
        >
          Join an Existing Office
        </Typography>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          To join an office, you'll need an invitation from your office
          administrator
        </Typography>
      </Box>

      {/* Instruction Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: '100%',
          maxWidth: 700
        }}
      >
        {/* Contact Admin Card */}
        <Card
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              gap: 2,
              p: 3
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <MailOutlineIcon
                sx={{
                  fontSize: 24,
                  color: 'primary.contrastText'
                }}
              />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                Contact Your Office Administrator
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6
                }}
              >
                Reach out to your office administrator or team lead and ask them
                to send you an invitation. They can do this from the office
                management section of the app.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* How Invites Work Card */}
        <Card
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              gap: 2,
              p: 3
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'secondary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 24,
                  color: 'primary.contrastText'
                }}
              />
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1
                }}
              >
                How Invites Work
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  mb: 1
                }}
              >
                Once your administrator sends an invitation:
              </Typography>
              <Box
                component='ul'
                sx={{
                  color: 'text.secondary',
                  pl: 2,
                  m: 0,
                  '& li': {
                    mb: 0.5,
                    fontSize: '0.875rem',
                    lineHeight: 1.6
                  }
                }}
              >
                <li>You'll receive a notification in the app</li>
                <li>
                  You can accept the invitation from your profile or
                  notifications
                </li>
                <li>
                  Once accepted, you'll have access to book desks in that office
                </li>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          width: '100%',
          maxWidth: 700,
          mt: 2
        }}
      >
        <Button
          variant='outlined'
          size='large'
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1.5,
            fontWeight: 500
          }}
        >
          Back to Choices
        </Button>
        <Button
          variant='contained'
          size='large'
          startIcon={<ExploreIcon />}
          onClick={onExit}
          sx={{
            flex: 1,
            textTransform: 'none',
            py: 1.5,
            fontWeight: 500
          }}
        >
          Exit and Explore
        </Button>
      </Box>
    </Box>
  )
}

export default JoinOfficeInstructionsStep
